import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ChevronLeft, Share2, Copy, CheckCircle, Clock, User, Phone, AlertCircle, Play } from 'lucide-react';

export default function QRCodePage({ orderData, onBack, onViewOrders, onSimulateScan }) {
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState('pending');

  const { base, material, weight, supplierInfo } = orderData || {};
  const estimatedAmount = weight ? (parseFloat(weight) * 2650).toFixed(2) : '0.00';

  const qrUrl = `${window.location.origin}/supplier-confirm?orderId=${Date.now()}`;

  useEffect(() => {
    const checkTimer = setInterval(() => {
      const confirmed = localStorage.getItem('supplierConfirmed');
      if (confirmed === 'true') {
        setStatus('confirmed');
        localStorage.removeItem('supplierConfirmed');
      }
    }, 2000);

    return () => clearInterval(checkTimer);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(qrUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: '废钢直通车 - 实供人确认订单',
          text: `请确认订单：${material}，预估重量${weight}吨，预估金额${estimatedAmount}元`,
          url: qrUrl
        });
      } catch (err) {
        console.log('分享取消');
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button onClick={onBack} className="p-1 -ml-2">
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <span className="font-bold text-slate-800 ml-2 text-lg">等待实供人确认</span>
      </div>

      <div className="p-4 space-y-4">
        <div className={`rounded-xl p-4 text-white shadow-md ${
          status === 'confirmed'
            ? 'bg-gradient-to-r from-green-500 to-green-600'
            : 'bg-gradient-to-r from-orange-500 to-orange-600'
        }`}>
          <div className="flex items-center gap-2 mb-1">
            {status === 'confirmed' ? (
              <>
                <CheckCircle size={16} />
                <span className="text-sm font-medium">实供人已确认</span>
              </>
            ) : (
              <>
                <Clock size={16} />
                <span className="text-sm font-medium">等待实供人扫码确认</span>
              </>
            )}
          </div>
          <div className="font-bold text-xl">
            {status === 'confirmed' ? '订单确认成功' : '二维码长期有效'}
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full" />
            <h3 className="font-bold text-slate-800">订单信息</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">收货基地</span>
              <span className="text-sm font-bold text-slate-800">{base?.name || '马钢诚兴马鞍山基地'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">物料名称</span>
              <span className="text-sm font-bold text-slate-800">{material || '重型废钢 (HMS1)'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">预估重量</span>
              <span className="text-sm font-bold text-slate-800">{weight} 吨</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">预估金额</span>
              <span className="text-sm font-bold text-red-600">{estimatedAmount} 元</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
          <div className="flex items-center gap-2 mb-3">
            <User size={16} className="text-orange-600" />
            <span className="font-bold text-orange-800">实供人信息</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-orange-700">姓名：</span>
              <span className="text-sm font-bold text-orange-900">{supplierInfo?.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={14} className="text-orange-600" />
              <span className="text-sm text-orange-700">手机号：</span>
              <span className="text-sm font-bold text-orange-900">{supplierInfo?.phone}</span>
            </div>
          </div>
        </div>

        {status === 'pending' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex flex-col items-center">
              <div className="mb-4 text-center">
                <p className="text-sm text-slate-600 mb-1">请让实供人扫描下方二维码</p>
                <p className="text-xs text-slate-400">扫码后需阅读并确认订单信息</p>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-lg border-2 border-slate-100 mb-4">
                <QRCodeSVG
                  value={qrUrl}
                  size={200}
                  level="H"
                  includeMargin={false}
                />
              </div>

              <div className="w-full space-y-3">
                <button
                  onClick={handleShare}
                  className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <Share2 size={18} />
                  分享给实供人
                </button>
                <button
                  onClick={handleCopy}
                  className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-bold text-base hover:bg-slate-200 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  {copied ? (
                    <>
                      <CheckCircle size={18} className="text-green-500" />
                      已复制链接
                    </>
                  ) : (
                    <>
                      <Copy size={18} />
                      复制链接
                    </>
                  )}
                </button>
                {onSimulateScan && (
                  <button
                    onClick={onSimulateScan}
                    className="w-full py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <Play size={18} />
                    模拟实供人扫码（测试）
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {status === 'confirmed' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-green-200">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle size={40} className="text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">实供人已确认订单</h3>
              <p className="text-sm text-slate-500 text-center mb-4">
                订单已创建成功，合同已自动生成
              </p>
              <button
                onClick={onViewOrders}
                className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
              >
                查看我的订单
              </button>
            </div>
          </div>
        )}

        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-blue-500 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700 leading-relaxed">
              <p className="font-bold mb-1">温馨提示</p>
              <ul className="space-y-1 text-xs">
                <li>• 请将二维码或链接发送给实供人</li>
                <li>• 二维码长期有效，可随时扫码确认</li>
                <li>• 实供人确认后订单才会正式生效</li>
                <li>• 实供人需完成实名认证和电子签约</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
